<template>
  <div class="slider">
    <div
      ref="carouselRef"
      class="tw-overflow-hidden"
    >
      <div class="tw-flex -tw-ml-4">
        <slot></slot>
      </div>
    </div>
    <div class="tw-p-6 tw-flex tw-justify-center">
      <div
        v-for="(_, index) in dots"
        :key="index"
        class="tw-w-[15px] tw-h-[15px] tw-inline-block tw-cursor-pointer tw-rounded-full tw-m-2"
        :class="index === selectedIndex ? 'tw-bg-[#828282]' : 'tw-bg-[#D9D9D9]'"
        @click="scrollTo(index)"
      ></div>
    </div>
  </div>
</template>

<script>
import EmblaCarousel from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'

export default {
  name: 'CarouselSlider',
  props: {
    autoplay: {
      type: Boolean,
      default: false,
    },
    autoplayTimeout: {
      type: Number,
      default: 4000,
    },
    animation: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      emblaApi: null,
      selectedIndex: 0,
      dots: [],
    }
  },
  mounted() {
    const carouselRef = this.$refs.carouselRef
    const options = { skipSnaps: true }
    const plugins = this.setupPlugins()
    this.emblaApi = EmblaCarousel(carouselRef, options, plugins)

    if (this.animation === 'opacity') {
      this.setupTweenOpacity(this.emblaApi)
    }

    this.emblaApi.on('select', () => {
      this.selectedIndex = this.emblaApi.selectedScrollSnap()
    })

    this.emblaApi.on('slidesInView', () => {
      this.dots = this.emblaApi.scrollSnapList()
    })
  },
  methods: {
    setupPlugins() {
      const plugins = []
      if (this.autoplay) {
        plugins.push(Autoplay({ delay: this.autoplayTimeout }))
      }
      return plugins
    },
    scrollTo(index) {
      this.emblaApi?.scrollTo(index)
    },
    setupTweenOpacity(emblaApi) {
      const TWEEN_FACTOR_BASE = 0.84
      let tweenFactor = 0

      const numberWithinRange = (number, min, max) =>
        Math.min(Math.max(number, min), max)

      const setTweenFactor = _emblaApi => {
        tweenFactor = TWEEN_FACTOR_BASE * _emblaApi.scrollSnapList().length
      }

      const tweenOpacity = (_emblaApi, eventName) => {
        const engine = _emblaApi.internalEngine()
        const scrollProgress = _emblaApi.scrollProgress()
        const slidesInView = _emblaApi.slidesInView()
        const isScrollEvent = eventName === 'scroll'

        _emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
          let diffToTarget = scrollSnap - scrollProgress
          const slidesInSnap = engine.slideRegistry[snapIndex]

          slidesInSnap.forEach(slideIndex => {
            if (isScrollEvent && !slidesInView.includes(slideIndex)) return

            if (engine.options.loop) {
              engine.slideLooper.loopPoints.forEach(loopItem => {
                const target = loopItem.target()

                if (slideIndex === loopItem.index && target !== 0) {
                  const sign = Math.sign(target)

                  if (sign === -1) {
                    diffToTarget = scrollSnap - (1 + scrollProgress)
                  }
                  if (sign === 1) {
                    diffToTarget = scrollSnap + (1 - scrollProgress)
                  }
                }
              })
            }

            const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor)
            const opacity = numberWithinRange(tweenValue, 0, 1).toString()
            _emblaApi.slideNodes()[slideIndex].style.opacity = opacity
          })
        })
      }

      const slideNodes = emblaApi.slideNodes()

      setTweenFactor(emblaApi)
      tweenOpacity(emblaApi)

      emblaApi
        .on('reInit', setTweenFactor)
        .on('reInit', tweenOpacity)
        .on('scroll', tweenOpacity)
        .on('slideFocus', tweenOpacity)

      return () => {
        slideNodes.forEach(slide => slide.removeAttribute('style'))
      }
    },
  },
}
</script>
